import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { isType } from '@sorare/core/src/lib/gql';

import { ScheduledPlayerGameScoreState } from 'components/game/ScheduledPlayerGameScoreState';
import { Grade } from 'components/player/Grade';
import { PlayerRotowireStatsProjection } from 'shared/player/PlayerDetails/PlayerStats/PlayerRotowireStatsProjection';
import { ProbablePitcher } from 'shared/player/ProbablePitcher';

import type {
  FixtureBarProjections_anyPlayer,
  FixtureBarProjections_playerGameScoreInterface,
} from './__generated__/index.graphql';

type Props = {
  player: Nullable<FixtureBarProjections_anyPlayer>;
  score: FixtureBarProjections_playerGameScoreInterface;
};

export const FixtureBarProjections = ({ player, score }: Props) => {
  const { anyPlayerGameStats, projection, anyGame } = score;
  if (isType(anyPlayerGameStats, 'PlayerGameStats')) {
    if (!projection) return null;
    return (
      <Vertical center>
        {!!projection && <Grade projection={projection} size={3} />}
        <ScheduledPlayerGameScoreState
          playerGameScores={[score]}
          variant="column-text"
          includesIcon
        />
      </Vertical>
    );
  }
  if (!player) return null;
  const isProbablePitcher =
    isType(anyGame, 'GameOfBaseball') &&
    anyGame.probablePitchers.some(pp => pp.slug === player.slug);
  return (
    <Vertical center>
      {!!isProbablePitcher && <ProbablePitcher />}
      <PlayerRotowireStatsProjection
        player={player}
        positions={player.anyPositions}
        variant="hexagon"
        playerGameScores={[score]}
      />
    </Vertical>
  );
};

FixtureBarProjections.fragments = {
  score: gql`
    fragment FixtureBarProjections_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      projection {
        id
        ...Grade_playerGameScoreProjection
      }
      anyPlayerGameStats {
        id
        ... on PlayerGameStats {
          id
        }
      }
      anyGame {
        id
        ... on GameOfBaseball {
          id
          probablePitchers {
            slug
          }
        }
      }
      ...PlayerRotowireStatsProjection_PlayerGameScoreInterface
      ...ScheduledPlayerGameScoreState_playerGameScore
    }
    ${Grade.fragments.projection}
    ${PlayerRotowireStatsProjection.fragments.playerGameScoreInterface}
    ${ScheduledPlayerGameScoreState.fragments.playerGameScore}
  ` as TypedDocumentNode<FixtureBarProjections_playerGameScoreInterface>,
  anyPlayer: gql`
    fragment FixtureBarProjections_anyPlayer on AnyPlayerInterface {
      slug
      anyPositions
      ...PlayerRotowireStatsProjection_AnyPlayerInterface
    }
    ${PlayerRotowireStatsProjection.fragments.anyPlayerInterface}
  ` as TypedDocumentNode<FixtureBarProjections_anyPlayer>,
};
